import {
  Badge,
  Button,
  Group,
  Input,
  Paper,
  Select,
  SimpleGrid,
  Skeleton,
  Stack
} from '@mantine/core'
import { isNotEmpty, useForm } from '@mantine/form'
import { minBy, sortBy } from 'lodash-es'
import { useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { GpuSelector } from '@/components/devices/GpuSelector/GpuSelector'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { useGetDeploymentOverview } from '@/queries/deploymentQueries'
import { useGetVlmModelList } from '@/queries/vlmQueries'
import { Device, DeviceSettings } from '@/types/device'
import { getDeviceGpus } from '@/utils/device/getDeviceAttr'
import { MAX_ITEMS_WITHOUT_PAGINATION } from '@/utils/pagination'
import { StorageLimitSlider } from './StorageLimitSlider'
import { MODEL_CARD_HEIGHT, VlmModelSelector } from './VlmModelSelector'
import { getGroupGpuIndexes } from './groupGpuIndexes'
import { getFallbackModel, toModelConfig } from './vlmModels'

const STORAGE_LIMIT_DEFAULT_GB = 256
const RETENTION_DEFAULT_DAYS = 90

const RETENTION_DAYS = [7, 30, 90, 180, 365]

const MODEL_SKELETON_COUNT = 3

export type SubmitFormValues = {
  payload: {
    gpuIndexes: number[]
    storageLimitGb: number
    retentionDays: number
    modelProfileId: string
  }
  // allows the caller to skip the API update when nothing changed
  isDirty: boolean
}

type FormValues = {
  gpuIndexes: number[]
  storageLimitGb: number
  retentionDays: string // Select requires string
  modelProfileId: string
}

type VlmDeviceSettingsFormProps = {
  device: Device
  settings?: DeviceSettings
  onSubmit: (values: SubmitFormValues) => void
  onCancel: () => void
}

export const VlmDeviceSettingsForm = ({
  device,
  settings,
  onSubmit,
  onCancel
}: VlmDeviceSettingsFormProps) => {
  const intl = useIntl()
  const gpus = getDeviceGpus(device)

  const {
    data: modelData,
    isLoading: isLoadingModels,
    error: modelsError
  } = useGetVlmModelList({ limit: MAX_ITEMS_WITHOUT_PAGINATION })

  const models = useMemo(() => modelData?.results ?? [], [modelData])

  const { data: overview } = useGetDeploymentOverview({
    device_id: device.id,
    limit: MAX_ITEMS_WITHOUT_PAGINATION
  })

  const groupGpuIndexes = useMemo(
    () => getGroupGpuIndexes(overview, device.id),
    [overview, device.id]
  )

  const retentionOptions = RETENTION_DAYS.map((d) => ({
    value: String(d),
    label: intl.formatMessage(
      { id: 'vlm.hardware.settings.retentionDays.value' },
      { days: d }
    )
  }))

  const gpuIndexes = gpus.map((gpu) => gpu.index)

  const form = useForm<FormValues>({
    initialValues: {
      gpuIndexes:
        settings?.gpu_indexes?.filter((i) => gpuIndexes.includes(i)) ??
        gpuIndexes.slice(0, 1),
      storageLimitGb: settings?.disk_space ?? STORAGE_LIMIT_DEFAULT_GB,
      retentionDays: String(settings?.retention_days ?? RETENTION_DEFAULT_DAYS),
      modelProfileId: settings?.model_profile.id ?? ''
    },
    validate: {
      gpuIndexes: (value) =>
        gpuIndexes.length > 0 && value.length === 0
          ? intl.formatMessage({ id: 'devices.gpu.required' })
          : null,
      modelProfileId: isNotEmpty(
        intl.formatMessage({ id: 'vlm.hardware.settings.model.required' })
      )
    }
  })

  // preselect the smallest model once the catalog arrives
  useEffect(() => {
    if (!form.values.modelProfileId) {
      form.setFieldValue(
        'modelProfileId',
        minBy(models, 'min_required_gpus')?.id ?? ''
      )
      form.resetDirty()
    }
  }, [models]) // eslint-disable-line react-hooks/exhaustive-deps

  // keeps the selected model runnable when GPUs are deselected
  const handleGpuChange = (selectedGpuIndexes: number[]) => {
    form.setFieldValue('gpuIndexes', selectedGpuIndexes)

    const gpuCount = selectedGpuIndexes.length
    const selectedModel = models.find(
      ({ id }) => id === form.values.modelProfileId
    )

    if (selectedModel && selectedModel.min_required_gpus > gpuCount) {
      form.setFieldValue(
        'modelProfileId',
        getFallbackModel(models, gpuCount)?.id ?? ''
      )
    }
  }

  const handleSubmit = ({
    gpuIndexes: selectedGpuIndexes,
    storageLimitGb,
    retentionDays,
    modelProfileId
  }: FormValues) => {
    onSubmit({
      payload: {
        gpuIndexes: selectedGpuIndexes,
        storageLimitGb,
        retentionDays: Number(retentionDays),
        modelProfileId
      },
      isDirty: form.isDirty()
    })
  }

  const selectedGroupGpus = form.values.gpuIndexes.filter((index) =>
    groupGpuIndexes.includes(index)
  )

  const renderModelField = () => {
    if (isLoadingModels) {
      return (
        <SimpleGrid cols={{ base: 1, sm: 2 }} my={4}>
          {Array.from({ length: MODEL_SKELETON_COUNT }).map((_, i) => (
            <Skeleton key={i} height={MODEL_CARD_HEIGHT} radius="md" />
          ))}
        </SimpleGrid>
      )
    }

    if (modelsError) {
      return (
        <PlatformAlert variant="error" my={4} compact>
          <FormattedMessage id="vlm.hardware.settings.model.loadError" />
        </PlatformAlert>
      )
    }

    return (
      <SimpleGrid cols={{ base: 1, sm: 2 }} my={4}>
        <VlmModelSelector
          models={sortBy(models, 'min_required_gpus').map(toModelConfig)}
          value={form.values.modelProfileId}
          selectedGpuCount={form.values.gpuIndexes.length}
          onChange={(value) => form.setFieldValue('modelProfileId', value)}
        />
      </SimpleGrid>
    )
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="xl">
        <Paper p="lg" bg="gray.1">
          <Stack gap="xl">
            {gpus.length > 0 ? (
              <GpuSelector
                gpus={gpus}
                value={form.values.gpuIndexes}
                label={<FormattedMessage id="devices.gpu.select" />}
                description={
                  <FormattedMessage id="devices.gpu.selectDescription" />
                }
                error={form.errors.gpuIndexes}
                renderDetails={(gpu) =>
                  groupGpuIndexes.includes(gpu.index) && (
                    <Badge size="xs" variant="light" color="orange">
                      <FormattedMessage id="vlm.hardware.settings.gpu.runsGroups" />
                    </Badge>
                  )
                }
                onChange={handleGpuChange}
              />
            ) : (
              <PlatformAlert variant="warning" compact>
                <FormattedMessage id="devices.gpu.notFound" />
              </PlatformAlert>
            )}

            {selectedGroupGpus.length > 0 && (
              <PlatformAlert variant="warning" compact>
                <FormattedMessage
                  id="vlm.hardware.settings.gpu.groupsWarning"
                  values={{
                    count: selectedGroupGpus.length,
                    gpus: selectedGroupGpus.join(', ')
                  }}
                />
              </PlatformAlert>
            )}

            <Input.Wrapper
              label={<FormattedMessage id="vlm.hardware.settings.model" />}
              description={
                <FormattedMessage id="vlm.hardware.settings.model.description" />
              }
              error={form.errors.modelProfileId}
            >
              {renderModelField()}
            </Input.Wrapper>

            <StorageLimitSlider {...form.getInputProps('storageLimitGb')} />

            <Select
              label={
                <FormattedMessage id="vlm.hardware.settings.retentionDays" />
              }
              description={
                <FormattedMessage id="vlm.hardware.settings.retentionDays.description" />
              }
              data={retentionOptions}
              allowDeselect={false}
              {...form.getInputProps('retentionDays')}
            />
          </Stack>
        </Paper>

        <Group justify="end">
          <Button miw={120} variant="default" onClick={onCancel}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button type="submit" miw={120} disabled={gpus.length === 0}>
            <FormattedMessage id="vlm.hardware.settings.save" />
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
